<template>
  <div class="breadcrumbs-wrapper">
    <HgBreadcrumb :links="breadcrumbs" :theme="platform" />
  </div>
</template>

<script setup lang="ts">
import { HgBreadcrumb, HgSupportedLanguage, SupportedPlatforms } from '@smg-real-estate/domus';
import type { GeoLocation, GeoLocationType } from '@smg-real-estate/geo-libs';
import { useHead } from '@unhead/vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { isPlatformIs24 as checkIsPlatformIs24 } from '@/helpers/Platform/platform';
import { countryTextMapping } from '@/helpers/SEO/metaInfoHelper';
import { getAgentBasePathWithLocation, getBasePathWithLocation } from '@/router';
import { useGeoStore } from '@/stores/geo';
import type { DirectoryType } from '@/types/directoryType';

interface BreadcrumbLink {
  name: string;
  link?: string;
}

const DIRECTORY_BASE_PATH_WITH_LOCATION: Record<
  DirectoryType,
  (language: HgSupportedLanguage, location?: string) => string
> = {
  agency: getBasePathWithLocation,
  agent: getAgentBasePathWithLocation,
};

const POSTCODE_LOCATION_TYPES: GeoLocationType[] = ['zip', 'ziparea'];
const CITY_PARENT_TYPES: GeoLocationType[] = ['place', 'municipality'];

const { directoryType, searchedLocations, includeStructuredData } = defineProps<{
  directoryType: DirectoryType;
  searchedLocations: GeoLocation[];
  includeStructuredData?: boolean;
}>();

const emitStructuredData = computed(() => includeStructuredData ?? directoryType === 'agency');

const getDirectoryBasePathWithLocation = (
  language: HgSupportedLanguage,
  location?: string,
): string => DIRECTORY_BASE_PATH_WITH_LOCATION[directoryType](language, location);

const { t, locale } = useI18n();
const geoStore = useGeoStore();

const isPlatformIs24 = computed(() => checkIsPlatformIs24());

const platform = computed<SupportedPlatforms>(() =>
  isPlatformIs24.value ? SupportedPlatforms.is24 : SupportedPlatforms.hg,
);

const isPostcodeLocation = (location: GeoLocation): boolean =>
  POSTCODE_LOCATION_TYPES.includes(location.type);

/**
 * Resolve SEO-relevant ancestors for the breadcrumb trail.
 * Non-postcode leaves only surface the canton; postcodes also surface
 * place/municipality. Intermediate region/district levels are skipped.
 */
const getParentLocations = (location: GeoLocation): GeoLocation[] => {
  if (location.type === 'canton') {
    return [];
  }

  if (!location.parentPaths || location.parentPaths.length === 0) {
    return [];
  }

  const parentPath = location.parentPaths[0];
  const parentIds = parentPath.filter(
    (id: string) => !id.startsWith('geo-zipcode') && !id.startsWith('geo-country'),
  );
  const resolvedParents = geoStore.getGeoLocationsById(parentIds);

  const allowedTypes: GeoLocationType[] = isPostcodeLocation(location)
    ? ['canton', ...CITY_PARENT_TYPES]
    : ['canton'];

  return sortLocationsByHierarchy(
    resolvedParents.filter((parent) => allowedTypes.includes(parent.type)),
  );
};

const sortLocationsByHierarchy = (locations: GeoLocation[]): GeoLocation[] => {
  const hierarchyOrder = [
    'country',
    'canton',
    'district',
    'region',
    'municipality',
    'place',
    'cityregion',
    'citydistrict',
    'zip',
    'ziparea',
  ];

  return [...locations].sort((a, b) => {
    const aIndex = hierarchyOrder.indexOf(a.type);
    const bIndex = hierarchyOrder.indexOf(b.type);
    return aIndex - bIndex;
  });
};

const generateLocationLinks = (locations: GeoLocation[]): BreadcrumbLink[] => {
  const sortedLocations = sortLocationsByHierarchy(locations);
  const language = locale.value as HgSupportedLanguage;

  return sortedLocations.map((location, index) => {
    const locationName = geoStore.getGeoLocationName(location.newId ?? location.id, language) ?? '';
    const urlName = location.urlNames[language] || location.urlNames[HgSupportedLanguage.DE];
    const isLast = index === sortedLocations.length - 1;

    return {
      name: locationName,
      link: isLast
        ? undefined
        : `${import.meta.env.VUE_APP_BASE_URL}${getDirectoryBasePathWithLocation(language, urlName)}`,
    };
  });
};

const homepageLink = computed<BreadcrumbLink>(() => ({
  name: t('breadcrumbs.homepage'),
  link: import.meta.env.VUE_APP_BASE_URL,
}));

const breadcrumbs = computed<BreadcrumbLink[]>(() => {
  const language = locale.value as HgSupportedLanguage;

  if (searchedLocations.length === 0) {
    return [
      homepageLink.value,
      {
        name: countryTextMapping[language],
      },
    ];
  }

  const directoryHubLink: BreadcrumbLink = {
    name: t(`breadcrumbs.${directoryType}Directory`),
    link: `${import.meta.env.VUE_APP_BASE_URL}${getDirectoryBasePathWithLocation(language)}`,
  };

  const primaryLocation = searchedLocations[0];
  const parentLocations = getParentLocations(primaryLocation);
  const allLocations = [...parentLocations, primaryLocation];
  const locationLinks = generateLocationLinks(allLocations);

  if (isPostcodeLocation(primaryLocation)) {
    return [homepageLink.value, ...locationLinks];
  }

  return [homepageLink.value, directoryHubLink, ...locationLinks];
});

const generateBreadcrumbStructuredData = () => {
  if (!emitStructuredData.value || breadcrumbs.value.length === 0) {
    return [];
  }

  const itemListElement = breadcrumbs.value.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.name,
    ...(crumb.link ? { item: crumb.link } : {}),
  }));

  return [
    {
      type: 'application/ld+json' as const,
      textContent: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement,
      }),
    },
  ];
};

const breadcrumbStructuredData = computed(() => generateBreadcrumbStructuredData());

useHead({
  script: breadcrumbStructuredData,
});
</script>

<style lang="scss" scoped>
.breadcrumbs-wrapper > :deep([class*='HgBreadcrumb_breadcrumb'] ol) {
  li:last-child {
    pointer-events: none;
  }
}
</style>
